import axios from "axios"
import { API_BASE_URL } from '../M_Components/M_Utils/services.config'

/**
 * Generic Entity API Service
 * Centralizes all Generic Entity API calls following WebBase BufferService pattern
 */
class GenericEntityService {
    constructor() {
        this.baseURL = API_BASE_URL.genericentity
    }

    // ==================== ENTITIES (Admin) ====================

    /**
     * Get all entities
     * @returns {Promise} List of entities
     */
    getEntities() {
        return axios.get(`${this.baseURL}/entities`, { headers: this._getHeaders() })
    }

    /**
     * Get request headers with authorization
     * @private
     */
    _getHeaders() {
        const token = localStorage.getItem('tokenMaeil') || localStorage.getItem('jwt');
        const headers = {
            'Content-Type': 'application/json'
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }

    /**
     * Get entity by ID
     * @param {string} entityId
     * @returns {Promise} Entity details
     */
    getEntityById(entityId) {
        return axios.get(`${this.baseURL}/entities/${entityId}`)
    }

    /**
     * Get entity by code
     * @param {string} entityCode
     * @returns {Promise} Entity details
     */
    getEntityByCode(entityCode) {
        return axios.get(`${this.baseURL}/entities/code/${entityCode}`)
    }

    /**
     * Create new entity
     * @param {Object} entity
     * @returns {Promise} Created entity
     */
    createEntity(entity) {
        return axios.post(`${this.baseURL}/entities`, entity)
    }

    /**
     * Update entity by ID
     * @param {string} entityId
     * @param {Object} entity
     * @returns {Promise} Updated entity
     */
    updateEntityById(entityId, entity) {
        return axios.put(`${this.baseURL}/entities/${entityId}`, entity)
    }

    /**
     * Delete entity by ID
     * @param {string} entityId
     * @returns {Promise}
     */
    deleteEntityById(entityId) {
        return axios.delete(`${this.baseURL}/entities/${entityId}`)
    }

    /**
     * Get extendable entities
     * @returns {Promise}
     */
    getExtendableEntities(){
        return axios.get(`${this.baseURL}/entities/extendableEntities`)
    }

    /**
     * Get reference entities
     * @returns {Promise}
     */
    getReferenceEntities(){
        return axios.get(`${this.baseURL}/entities/referenceEntities`)
    }
    
    /**
     * Get extension entity by extendable entityId
     * @param {string} entityId
     * @param {string?} itemId
     * @returns {Promise}
     */
    getExtensionEntity(entityId, itemId) {
        return axios.get(`${this.baseURL}/entities/extension/${entityId}`, {
            params: itemId ? { id: itemId } : {}
        })
    }

    
    /**
     * Delete extension item by extendable entityId and itemId
     * @param {string} entityId
     * @param {string} itemId
     * @returns {Promise}
     */
    deleteExtensionItem(entityId, itemId) {
        return axios.delete(`${this.baseURL}/entities/extension/${entityId}/${itemId}`)
    }
    
    // ==================== FIELDS (Admin) ====================

    /**
     * Create field in entity
     * @param {string} entityId
     * @param {Object} fieldData
     * @returns {Promise} Created field
     */
    createEntityField(entityId, fieldData) {
        return axios.post(`${this.baseURL}/entities/${entityId}/field`, fieldData)
    }

    /**
     * Update field in entity
     * @param {string} entityId
     * @param {string} fieldId
     * @param {Object} fieldData
     * @returns {Promise} Updated field
     */
    updateEntityField(entityId, fieldId, fieldData) {
        return axios.put(`${this.baseURL}/entities/${entityId}/field/${fieldId}`, fieldData)
    }

    /**
     * Delete field from entity
     * @param {string} entityId
     * @param {string} fieldId
     * @returns {Promise}
     */
    deleteEntityField(entityId, fieldId) {
        return axios.delete(`${this.baseURL}/entities/${entityId}/field/${fieldId}`)
    }

    /**
     * Reorder entity fields
     * @param {string} entityId
     * @param {Array} fields
     * @returns {Promise}
     */
    reorderEntityFields(entityId, fields) {
        return axios.put(`${this.baseURL}/entities/${entityId}/field/order`, fields)
    }

    // ==================== ITEMS (Data) ====================

    /**
     * Get all items from entity
     * @param {string} entityId
     * @returns {Promise} List of items
     */
    getItemsByEntity(entityId) {
        return axios.get(`${this.baseURL}/entities/${entityId}/items`)
    }

    /**
     * Get items from entity with OData pagination, filtering and sorting
     * @param {string} entityId
     * @param {number} top - Number of records to return (page size)
     * @param {number} skip - Number of records to skip (offset)
     * @param {string} orderBy - OData orderby clause (e.g., "name desc")
     * @param {string} filter - OData filter clause (e.g., "name eq 'test'")
     * @param {boolean} count - Whether to include total count
     * @returns {Promise} Paginated response with @odata.count
     */
    getItemsByEntityPaginated(entityId, top = 20, skip = 0, orderBy = null, filter = null, count = true) {
        const params = new URLSearchParams();
        
        if (top) params.append('$top', top);
        if (skip) params.append('$skip', skip);
        if (orderBy) params.append('$orderby', orderBy);
        if (filter) params.append('$filter', filter);
        if (count) params.append('$count', 'true');
        
        const queryString = params.toString();
        return axios.get(`${this.baseURL}/entities/${entityId}/items${queryString ? '?' + queryString : ''}`);
    }

    /**
     * Get item by ID
     * @param {string} entityId
     * @param {string} itemId
     * @returns {Promise} Item details
     */
    getItemById(entityId, itemId) {
        return axios.get(`${this.baseURL}/entities/${entityId}/items/${itemId}`)
    }

    /**
     * Create new item
     * @param {string} entityId
     * @param {Object|FormData} data - Item data as object (JSON) or FormData for file uploads
     * @param {boolean} isFormData - True if data is FormData with files
     * @returns {Promise} Created item
     */
    createItem(entityId, data, isFormData = false) {
        if (isFormData) {
            return axios.post(
                `${this.baseURL}/entities/${entityId}/items`,
                data,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
        } else {
            // Send as JSON to preserve number types
            return axios.post(
                `${this.baseURL}/entities/${entityId}/items`,
                data,
                { headers: { "Content-Type": "application/json" } }
            );
        }
    }

    /**
     * Update item by ID
     * @param {string} entityId
     * @param {string} itemId
     * @param {Object|FormData} data - Item data as object or FormData for file uploads
     * @returns {Promise} Updated item
     */
    updateItemById(entityId, itemId, data) {
        // If data is FormData (has append method), send as multipart/form-data
        // Otherwise send as JSON
        const isFormData = data instanceof FormData || (data && typeof data.append === 'function');

        return axios.put(
            `${this.baseURL}/entities/${entityId}/items/${itemId}`,
            data,
            {
                headers: isFormData
                    ? { "Content-Type": "multipart/form-data" }
                    : { "Content-Type": "application/json" }
            }
        )
    }

    /**
     * Delete item by ID
     * @param {string} entityId
     * @param {string} itemId
     * @returns {Promise}
     */
    deleteItemById(entityId, itemId) {
        return axios.delete(`${this.baseURL}/entities/${entityId}/items/${itemId}`)
    }

    /**
     * Delete multiple items by IDs (performs individual delete calls)
     * @param {string} entityId
     * @param {Array<string>} itemIds - Array of item IDs to delete
     * @returns {Promise<Array>} Array of results for each delete operation
     */
    async deleteMultipleItems(entityId, itemIds) {
        const deletePromises = itemIds.map(itemId => 
            this.deleteItemById(entityId, itemId)
                .then(res => ({ success: true, itemId, response: res }))
                .catch(err => ({ success: false, itemId, error: err }))
        );
        return Promise.all(deletePromises);
    }

    /**
     * Get relation items
     * @param {string} primaryEntityId
     * @param {string} fieldId
     * @param {string|null} itemId - Optional, for editing existing item
     * @returns {Promise} Relation items
     */
    getRelationItemById(primaryEntityId, fieldId, itemId = null) {
        const itemPath = itemId ? `/${itemId}` : ''
        return axios.get(`${this.baseURL}/entities/${primaryEntityId}/items/${fieldId}/relations${itemPath}`)
    }

    /**
     * Get reference columns items to populate dropdown
     * @param {string} entityId
     * @param {string} fieldId
     * @returns {Promise} Reference items
     */
    getReferenceColumnDropdownItems(entityId, fieldId){
        return axios.get(`${this.baseURL}/entities/${entityId}/items/${fieldId}/reference`)
    }

     /**
     * Checks if WT item is being used by GE reference field
     * @param {string} referenceEntityId
     * @param {guid} itemId 
     * @returns {boolean} true if item is in use
     */
    getReferenceItemUsage(referenceEntityId, itemId){
        return axios.get(`${this.baseURL}/entities/${referenceEntityId}/${itemId}/usage`)
    }
    
     /**
     * Returns GE items that have a reference to the WT item
     * @param {string} referenceEntityId
     * @param {guid} itemId 
     * @param {int} entityId - optional only get the data for the entity matching this id
     * @returns {Promise} Referenced items
     */
    getReferencedItems(referenceEntityId, itemId, entityId = null){
        return axios.get(`${this.baseURL}/entities/referenced/${referenceEntityId}/${itemId}`, entityId !== undefined ? { params: { entityId: entityId } } : null)
    }

     /**
     * Returns a list of GE entities that have a reference to a WT entity
     * @param {string} referenceEntityId
     * @returns {Promise} Referenced items
     */
    getReferencedEntities(referenceEntityId){
        return axios.get(`${this.baseURL}/entities/referenced/${referenceEntityId}`)
    }
    // ==================== ATTACHMENTS ====================

    /**
     * Download attachment file
     * @param {string} attachmentId
     * @returns {Promise} Blob file
     */
    getFile(attachmentId) {
        return axios.get(`${this.baseURL}/attachment/${attachmentId}`, { responseType: 'blob' })
    }

    // ==================== MENU ====================

    /**
     * Get menu options (entities to show in menu)
     * @returns {Promise} Menu options with graceful 401/403 handling
     */
    getMenuOptions() {
        return axios.get(`${this.baseURL}/menu`, { headers: this._getHeaders() })
            .catch(err => {
                // Graceful degradation: return empty array if user lacks permissions
                if (err.response?.status === 401 || err.response?.status === 403) {
                    return { data: [] }
                }
                throw err
            })
    }

    // ==================== LANGUAGES ====================

    /**
     * Get available languages for captions
     * @returns {Promise} List of language codes
     */
    getLanguages() {
        return axios.get(`${this.baseURL}/language`)
    }
}

// Export singleton instance
export const genericEntityService = new GenericEntityService()

// Export class for testing/extension
export default GenericEntityService
